import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  query,
  where,
} from "firebase/firestore"
import { deleteObject, ref } from "firebase/storage"

import { AlertModal } from "../../../components"
import PageWrapper from "../../../components/PageWrapper/PageWrapper"
import arrowLeft from "../../../assets/icons/Arrows/arrow-narrow-left.png"
import { storage } from "../../../configs/firebase"
import trashIcon from "../../../assets/icons/General/trash-01.png"
import { useAuth } from "../../../contexts/Auth/AuthContext"
import useMyDocuments from "./hooks/useMyDocuments"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { useTranslation } from "react-i18next"

const MyDocuments = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const documents = useMyDocuments(user?.uid)

  const db = getFirestore()

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [docToDelete, setDocToDelete] = useState(null)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")

  const handleDeleteDocument = async (documentId) => {
    try {
      // Find the document to get storage info
      const documentToDelete = documents.find((d) => d._id === documentId)

      // Try to delete from Storage first; proceed even if it fails
      try {
        if (documentToDelete?.userId && documentToDelete?.uniqueFileName) {
          const storagePath = `documents/${documentToDelete.userId}/${documentToDelete.uniqueFileName}`
          const storageRef = ref(storage, storagePath)
          await deleteObject(storageRef)
        }
      } catch (e) {
        console.warn("Failed to delete file from storage:", e)
        // Continue to Firestore deletion even if storage deletion fails
      }

      // Delete related sharing documents
      try {
        const sharingQuery = query(
          collection(db, "sharingDocuments"),
          where("documentId", "==", documentId)
        )
        const sharingDocs = await getDocs(sharingQuery)

        // Delete all sharing documents
        const deletePromises = sharingDocs.docs.map((doc) => deleteDoc(doc.ref))
        await Promise.all(deletePromises)

        console.log(`Deleted ${sharingDocs.docs.length} sharing documents`)
      } catch (e) {
        console.warn("Failed to delete sharing documents:", e)
        // Continue to main document deletion even if sharing deletion fails
      }

      // Delete from Firestore
      await deleteDoc(doc(db, "documents", documentId))
    } catch (error) {
      console.error("Failed to delete document", error)
      throw error
    }
  }

  const handleOpen = (id) => navigate(`/my-document/${id}`)

  return (
    <PageWrapper
      title={t("settings.myDocuments")}
      headerLeft={
        <button
          className="absolute left-0 inset-y-0 my-auto w-12 h-12 flex items-center justify-center z-10"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          <img src={arrowLeft} alt="back" className="w-6 h-6 invert" />
        </button>
      }
      background="bg-dark-default"
    >
      <div className="bg-light-default rounded-[16px] h-full flex flex-col px-3">
        {documents.length === 0 ? (
          <div className="py-5 text-center text-black-t-lighten-2 text-[16px]">
            {t("documents.emptyState")}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto scrollbar-hide">
            {documents.map((d, idx) => (
              <div key={d._id}>
                <div className="relative group flex items-center justify-between h-[72px] px-2 hover:bg-white-t-lighten-6 rounded-[8px] cursor-pointer">
                  <div
                    className="min-w-0 mr-3 cursor-pointer"
                    onClick={() => handleOpen(d._id)}
                  >
                    <div className="text-[16px] font-semibold text-dark-default truncate">
                      {d.fileName}
                    </div>
                    <div className="text-[12px] text-black-t-lighten-2 mt-0.5 truncate">
                      {(d._createdAt?.toDate
                        ? d._createdAt.toDate()
                        : new Date(d._createdAt || Date.now())
                      ).toLocaleDateString()}{" "}
                      • {((d.size || 0) / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                  <span className="w-5 h-5" />
                  <button
                    onClick={() => {
                      setDocToDelete(d._id)
                      setConfirmOpen(true)
                    }}
                    className="absolute right-2 bg-danger-default text-light-default w-[40px] h-[40px] rounded-[10px] flex items-center justify-center cursor-pointer"
                    aria-label="Delete"
                  >
                    <img
                      src={trashIcon}
                      alt="delete"
                      className="w-5 h-5 invert"
                    />
                  </button>
                </div>
                {idx < documents.length - 1 && (
                  <div className="h-px bg-black-t-lighten-5 mx-2" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertModal
        open={confirmOpen}
        title={t("documents.deleteTitle")}
        message={t("documents.deleteConfirm")}
        onClose={() => setConfirmOpen(false)}
        actions={[
          { label: t("common.cancel"), variant: "default" },
          {
            label: t("common.delete"),
            variant: "destructive",
            onClick: async () => {
              try {
                await handleDeleteDocument(docToDelete)
              } catch (e) {
                console.error("Failed to delete document", e)
                setErrorMsg(t("documents.deleteErrorMessage"))
                setErrorOpen(true)
              } finally {
                setConfirmOpen(false)
                setDocToDelete(null)
              }
            },
          },
        ]}
      />

      <AlertModal
        open={errorOpen}
        title={t("common.error")}
        message={errorMsg}
        onClose={() => setErrorOpen(false)}
      />
    </PageWrapper>
  )
}

export default MyDocuments
